import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';

import { mergeMap, mergeMapTo } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import {
	IS_NOT_EMPTY,
	NOTIFICATION_MESSAGE,
	NOTIFICATION_TYPES,
	RestService,
} from 'cargo-chief-commons';
import { UrlService } from '../url/url.service';
import { SharedService } from '../shared/shared.service';
import { PushNotificationService } from '../pushNotification/push-notification.service';
import { Cargochief } from '../cargochief/cargochief.service';
import { UtilService } from '../util/util.service';

@Injectable({
	providedIn: 'root',
})
export class FirebaseService {
	private notificationData = new Subject<any>();

	constructor(
		private afMessaging: AngularFireMessaging,
		private urlService: UrlService,
		private restService: RestService,
		private sharedService: SharedService,
		private pushNotificationService: PushNotificationService,
		private cargochief: Cargochief,
		public utilService: UtilService
	) {}

	requestPermission() {
		if (
			localStorage.getItem('isForegroundEnabled') == undefined ||
			localStorage.getItem('isForegroundEnabled') == null ||
			!JSON.parse(localStorage.getItem('isForegroundEnabled'))
		) {
			this.afMessaging.requestPermission.pipe(mergeMapTo(this.afMessaging.tokenChanges)).subscribe(
				(token) => {
					this.listen();
					localStorage.setItem('isForegroundEnabled', 'true');
					if (
						this.cargochief.getFirebaseToken() == undefined ||
						this.cargochief.getFirebaseToken() == null
					) {
						localStorage.setItem('afToken', token);
						this.registerDevice();
					} else {
						if (token !== this.cargochief.getFirebaseToken()) {
							this.updateDevice(token);
						}
					}
				},
				(error) => {
					console.error(error);
				}
			);
		}
	}

	listen() {
		this.afMessaging.messages.subscribe((message) => {
			const notificationType = message['data']['notification.name'];
			this.sendNotificationData(message['data'], notificationType);
			console.log('firebase msg: ', message);
		});
	}

	sendNotificationData(data: any, notificationType: string) {
		let sendPushNotification = true;
		let notificationMessage: Array<any> = [NOTIFICATION_MESSAGE[notificationType]];
		switch (notificationType) {
			case NOTIFICATION_TYPES.ProjectRateReceivedEmailCommand:
				notificationMessage[0].title = `${data['notification.carrier.businessName']} submitted project rate.`;
				break;
			case NOTIFICATION_TYPES.TaskAssignedEmailCommand:
				notificationMessage[0].title = `${data['notification.assigneeName']} assigned task to you.`;
				break;
			case NOTIFICATION_TYPES.ProjectOwnerEmailCommand:
				notificationMessage[0].title = `${data['notification.userName']} assigned project to you.`;
				break;
			case NOTIFICATION_TYPES.UserCarrierMapAssignedEmailCommand:
				notificationMessage[0].title = `${data['notification.businessName']} assigned to you.`;
				break;
			case NOTIFICATION_TYPES.CheckProjectCommand:
				notificationMessage[0].title = `${data['notification.businessName']} request ${data['project.name']}.`;
				break;

			case NOTIFICATION_TYPES.CarrierExcludedEmailCommand:
				notificationMessage[0].title = `${data['notification.carrierBusinessName']} excluded.`;
				break;
			
			case NOTIFICATION_TYPES.LoadFileReceivedCommand:
				notificationMessage[0].title = `Load file ${data['notification.fileName']} received at ${data['notification.fileReceivedAt']} is being processed.`;
				break;

			case NOTIFICATION_TYPES.ProjectBestRateUpdatedCommand:
			case NOTIFICATION_TYPES.CalculateProbabilityCommand:
			case NOTIFICATION_TYPES.CarrierOpenedEmail:
			case NOTIFICATION_TYPES.DownloadCarriersEmailCommand:
				sendPushNotification = false;
				break;
			default:
				break;
		}
		if (sendPushNotification) {
			if (NOTIFICATION_TYPES.TaskAssignedEmailCommand) {
				notificationMessage[0]['url'] = 'users/tasks';
			} else if (
				NOTIFICATION_TYPES.ProjectOwnerEmailCommand ||
				NOTIFICATION_TYPES.CheckProjectCommand
			) {
				notificationMessage[0][
					'url'
				] = `projects/project_details/${data['notification.projectId']}`;
			} else if (NOTIFICATION_TYPES.UserCarrierMapAssignedEmailCommand) {
				notificationMessage[0]['url'] = `carriers/${data['notification.carrierId']}`;
			}
			this.pushNotificationService.generateNotification(notificationMessage);
		}
		if (
			notificationType !== NOTIFICATION_TYPES.LoadPostedCommand &&
			notificationType != NOTIFICATION_TYPES.ProjectRateReceivedEmailCommand &&
			notificationType != NOTIFICATION_TYPES.TaskAssignedEmailCommand &&
			notificationType != NOTIFICATION_TYPES.ProjectBestRateUpdatedCommand &&
			notificationType != NOTIFICATION_TYPES.CalculateProbabilityCommand &&
			notificationType != NOTIFICATION_TYPES.CarrierOpenedEmail &&
			notificationType != NOTIFICATION_TYPES.CarrierNotAvailableRFPCommand &&
			notificationType != NOTIFICATION_TYPES.DownloadCarriersEmailCommand &&
			notificationType !== NOTIFICATION_TYPES.CarrierExcludedEmailCommand &&
			notificationType !== NOTIFICATION_TYPES.LoadFileReceivedCommand
		) {
			this.notificationData.next(this.sharedService.buildObjectForBidNotification(data));
		}

		if (
			notificationType == NOTIFICATION_TYPES.ProjectRateReceivedEmailCommand ||
			notificationType == NOTIFICATION_TYPES.TaskAssignedEmailCommand ||
			notificationType == NOTIFICATION_TYPES.ProjectBestRateUpdatedCommand ||
			notificationType == NOTIFICATION_TYPES.CalculateProbabilityCommand ||
			notificationType == NOTIFICATION_TYPES.CarrierOpenedEmail ||
			notificationType == NOTIFICATION_TYPES.CarrierNotAvailableRFPCommand ||
			notificationType == NOTIFICATION_TYPES.CarrierExcludedEmailCommand ||
			notificationType == NOTIFICATION_TYPES.LoadFileReceivedCommand
		) {
			this.notificationData.next(data);
		}

		if (notificationType == NOTIFICATION_TYPES.DownloadCarriersEmailCommand) {
			// this.utilService.showSnackbar(`Your mapped carrier download is in your inbox`, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
			this.notificationData.next({
				fileUrl: data['notification.fileUrl'],
			});
		}
	}

	getNotificationData(): Observable<any> {
		return this.notificationData.asObservable();
	}

	deleteToken() {
		this.afMessaging.getToken
			.pipe(mergeMap((token) => this.afMessaging.deleteToken(token)))
			.subscribe((token) => {
				console.log('Deleted!');
			});
	}

	registerDevice() {
		this.postDeviceRequest(this.urlService.registerDeviceUrl());
	}

	updateDevice(token) {
		if (this.cargochief.getDeviceId()) {
			localStorage.setItem('afToken', token);
			this.postDeviceRequest(this.urlService.updateDeviceUrl());
		}
	}

	postDeviceRequest(url: string) {
		const deviceId = this.cargochief.getDeviceId(),
			webSocketToken = this.cargochief.getFirebaseToken(),
			userId = this.cargochief.USER_ID;
		if (
			!this.utilService.isNullOrUndefined(deviceId) &&
			IS_NOT_EMPTY(deviceId) &&
			!this.utilService.isNullOrUndefined(webSocketToken) &&
			IS_NOT_EMPTY(webSocketToken) &&
			!this.utilService.isNullOrUndefined(userId)
		) {
			const data = {
				deviceId: deviceId,
				webSocketToken: webSocketToken,
				userId: userId,
			};
			this.restService.POST(url, data).subscribe((result) => {
				console.log('device registration successful. : ', result);
			});
		}
	}
}
